// React
import { Fragment, useContext } from "react"

// Components
import { BaseTable, Button, Card, DateInput, FooterMenu, HeaderMenu, Loading, Select } from "components"
import { ExportPdf } from "./components"

// Contexts
import { AccountContext } from "./contexts"

// Icons
import { TbPlus, TbX } from "react-icons/tb"

// Third-Party Libraries
import moment from "moment"

// Types
import type { SelectOptionType } from "types"

// Utils
import { convertNumber, exportPdf, useAccounts, useHandleAuth, useLocation } from "utils"
import { exportExcel, getSummary, useList, useSelectValue } from "./utils"

export default function TransactionDetail(): JSX.Element {
  // Hooks
  const accounts = useAccounts()
  const auth = useHandleAuth()
  const token = auth.userTokenData()
  const { data, filter, isLoading, fetchData, onAddCoa, onChangeFilter, onRemoveCoa } = useList()

  // Vars
  const hasData: boolean = Boolean(data.length)
  const summary = getSummary(data)

  return (
    <section className="my-3 flex flex-col gap-3">
      <HeaderMenu title="TRANSACTION DETAIL REPORT" />

      <AccountContext.Provider value={accounts}>
        <Card>
          <Card.Body>
            <section className="flex flex-col gap-3">
              <section className="flex flex-wrap items-end gap-3">
                <section className="w-[350px] flex items-end gap-1">
                  <Account onChange={onAddCoa} />
                </section>

                <DateInput
                  className="w-[200px]"
                  label="DATE RANGE"
                  selected={moment(filter.start_date).toDate()}
                  onChange={res => onChangeFilter("start_date", moment(res).format("YYYY-MM-DD"))}
                />

                <DateInput
                  className="w-[200px]"
                  label=""
                  selected={moment(filter.end_date).toDate()}
                  onChange={res => onChangeFilter("end_date", moment(res).format("YYYY-MM-DD"))}
                />

                <div className="w-[200px]">
                  <Location
                    value={filter.location_id}
                    onChange={value => onChangeFilter("location_id", value)}
                  />
                </div>

                <Button
                  color="primary"
                  onClick={fetchData}
                >
                  UPDATE
                </Button>
              </section>

              <section className="flex flex-wrap gap-x-3 gap-y-1">
                <SelectedAccount
                  value={filter.coa}
                  onClick={onRemoveCoa}
                />
              </section>
            </section>
          </Card.Body>
        </Card>
      </AccountContext.Provider>

      <BaseTable>
        <tbody>
          {isLoading || !hasData ? (
            <tr>
              <td colSpan={10}>
                <Loading
                  loading={isLoading}
                  errorText="No data available"
                />
              </td>
            </tr>
          ) : filter.coa.map((item, key) => {
            // Vars
            const selectedCoa = accounts.data.find(i => i.value === item)?.label ?? "-"
            const selectedData = data.filter(i => i.coa_id === item)
            const summary = getSummary(selectedData)

            return (
              <Fragment key={key}>
                <tr>
                  <td
                    colSpan={10}
                    className="text-lg font-bold"
                  >
                    ACCOUNT : {selectedCoa}
                  </td>
                </tr>

                <tr className="text-center">
                  <th>DATE</th>
                  <th>NUMBER</th>
                  <th>ACCOUNT</th>
                  <th>SOURCE</th>
                  <th>GROUP</th>
                  <th>REFERENCE</th>
                  <th>DESCRIPTION</th>
                  <th>REFERENCE</th>
                  <th>DEBET</th>
                  <th>CREDIT</th>
                </tr>

                {!selectedData.length ? (
                  <tr>
                    <td colSpan={10}>
                      <Loading errorText="No data available" />
                    </td>
                  </tr>
                ) : selectedData.map((item, key) => (
                  <tr key={key}>
                    <td>{item.transaction_date}</td>
                    <td>{item.coa_code}</td>
                    <td>{item.coa_name}</td>
                    <td>{item.source}</td>
                    <td>{item.group}</td>
                    <td>{item.contact}</td>
                    <td>{item.description}</td>
                    <td>{item.referensi}</td>
                    <td className="text-right">{convertNumber(item.debit).intoCurrency}</td>
                    <td className="text-right">{convertNumber(item.credit).intoCurrency}</td>
                  </tr>
                ))}

                <tr className="font-bold">
                  <td
                    className="text-right"
                    colSpan={8}
                  >
                    Total
                  </td>

                  <td className="text-right">{convertNumber(summary.debit).intoCurrency}</td>
                  <td className="text-right">{convertNumber(summary.credit).intoCurrency}</td>
                </tr>
              </Fragment>
            )
          })}

          {hasData && (
            <tr className="text-lg font-bold">
              <td
                className="text-right"
                colSpan={8}
              >
                GRAND TOTAL
              </td>

              <td>
                <div>DEBET</div>
                <div className="text-right">{convertNumber(summary.debit).intoCurrency}</div>
              </td>

              <td>
                <div>CREDIT</div>
                <div className="text-right">{convertNumber(summary.credit).intoCurrency}</div>
              </td>
            </tr>
          )}
        </tbody>
      </BaseTable>

      <FooterMenu
        export={{
          actiontype: "export",
          onClick: () => exportExcel(data, accounts.data, filter.coa)
        }}
        pdf={{
          actiontype: "pdf",
          onClick: () => {
            exportPdf({
              fileName: "Transaction Detail",
              documentComponent: (
                <ExportPdf
                  title="Transaction Detail"
                  coaData={accounts.data}
                  coaFilter={filter.coa}
                  companyName={token?.company_name}
                  data={data}
                />
              )
            })
          }
        }}
      />
    </section>
  )
}

function Account(params: {
  onChange: (value: string) => void
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useContext(AccountContext)
  const { value, onChange } = useSelectValue()

  return (
    <>
      <Select
        label="ACCOUNT"
        placeholder="Select Account"
        isLoading={isLoading}
        options={data}
        value={data.find(item => item.value === value) ?? null}
        onChange={res => {
          if (res) {
            onChange(res.value)
          }
        }}
      />

      <Button
        color="primary"
        onClick={() => params.onChange(value)}
      >
        <TbPlus />
      </Button>
    </>
  )
}

function Location(params: {
  value: string
  onChange: (value: string) => void
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useLocation()

  // Functions
  const getData = (): SelectOptionType[] => {
    return [
      {
        label: "All",
        value: "0"
      },
      ...data
    ]
  }

  return (
    <Select
      label="LOCATION"
      placeholder="Select Location"
      isLoading={isLoading}
      options={getData()}
      value={getData().find(item => item.value === params.value)}
      onChange={res => {
        if (res) {
          params.onChange(res.value)
        }
      }}
    />
  )
}

function SelectedAccount(params: {
  value: string[]
  onClick: (value: string) => void
}): JSX.Element {
  // Hooks
  const { data } = useContext(AccountContext)

  return (
    <>
      {params.value.map((item, key) => {
        // Vars
        const selectedCoa: string = data.find(i => i.value === item)?.label ?? "-"

        return (
          <button
            key={key}
            className="border w-[250px] p-2 flex justify-between items-center"
            onClick={() => params.onClick(item)}
          >
            <div className="grow text-left truncate">{selectedCoa}</div>
            <div className="divider divider-horizontal divider-neutral" />
            <TbX className="shrink-0" />
          </button>
        )
      })}
    </>
  )
}